import { LitElement, html, css, PropertyValues, unsafeCSS } from 'lit';
import { customElement, state, property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { FindYourSenatorDataService } from './dataService';
import type { SenatorInfo, AreaGeoJSON, AreaFeature, PlaceLocation } from './types';
import { findDistrictAtPoint } from './pointInPolygon';
import { findPlaceAtPoint, findDistrictsIntersectingPlace } from './intersection.ts';
import findYourSenatorStyles from '../../cssstyles/components/_findYourSenator.scss?inline';
import SearchStyles from '../../cssstyles/pages/_search.scss?inline';

import maplibregl from 'maplibre-gl';

const PHOTO_PLACEHOLDER_PATH: string = '/images/photoplaceholder.jpg';

/**
 * Interactive map component for finding Michigan State Senators
 * Features:
 * - Display all 38 senate district boundaries
 * - Hover highlighting of districts
 * - Click to select district and show senator info
 * - Search by address/city with Google Places autocomplete
 */
@customElement('find-your-senator-map')
export class FindYourSenatorMap extends LitElement {

    /** Google Maps API key for Places autocomplete */
    @property({ type: String, attribute: 'google-api-key' })
    googleApiKey = '';

    /** Currently selected district number */
    @state()
    private selectedDistrict: string | null = null;

    /** Senator info for selected district */
    @state()
    private declare selectedSenator: SenatorInfo | null;

    /** Loading state for the senator info panel */
    @state()
    private isLoading = false;

    /** Loading overlay shown on the map while URL parameters are being applied */
    @state()
    private isMapLoading = false;

    /** Whether the map has been panned/zoomed away from the initial view */
    @state()
    private isMapMoved = false;

    /** Whether the senator panel is visible */
    @state()
    private declare isPanelOpen: boolean;

    /** Error message */
    @state()
    private errorMessage = '';

    /** Search input value */
    @state()
    private searchValue = '';

    /** Disables the search button after a suggestion has been picked, until the input changes */
    @state()
    private isSearchDisabled = false;

    /** Autocomplete suggestions */
    @state()
    private declare suggestions: google.maps.places.PlacePrediction[];

    /** True when the API returned results but none were in Michigan */
    @state()
    private noMichiganResults = false;


    /** Active suggestion index for keyboard navigation */
    @state()
    private declare activeSuggestionIndex: number;

    /** Senators for the multi-district city view (empty for single-senator mode). */
    @state()
    private declare selectedSenators: SenatorInfo[];

    /** Display name of the city/township that produced the multi-senator view. */
    @state()
    private multiSenatorPlace: string | null = null;

    /** Sorted district names used to populate the keyboard-accessible select fallback. SC 2.1.1 */
    @state()
    private districtNames: string[] = [];

    /** Districts highlighted in red for the active multi-senator selection. */
    private multiSelectedDistricts: string[] = [];

    /** Maps each district name to its assigned colorblind-safe color for the current multi-selection. */
    private districtColorMap: Map<string, string> = new Map();

    /**
     * Colorblind-safe fill colors (Wong palette) assigned to each district
     * in the multi-selection, always in the same order.
     */
    private readonly multiSelectionColors: readonly string[] = [
        '#CC79A7', // reddish purple 
        '#009E73', // bluish green
        '#BE185D', //Hot Pink / Magenta  
        '#56B4E9', // sky blue      
        '#F0E442', // yellow
        '#6B21A8', // purple
        '#6A4B44', // a brown
        '#C2410C', //Deep Orange     
    ];

    private readonly legitimateCompanyBotUserAgents: readonly string[] = [
        'googlebot',
        'google-inspectiontool',
        'googleother',
        'adsbot-google',
        'mediapartners-google',
        'bingbot',
        'adidxbot',
        'msnbot',
        'duckduckbot',
        'applebot',
        'baiduspider',
        'yandexbot',
        'yandeximages',
        'yandexaccessibilitybot',
        'yandexmobilebot',
        'yandexpagechecker',
        'slurp',
        'sogou web spider',
        'sogou pic spider',
        'exabot',
        'seznambot',
        'petalbot',
        'bytespider',
        'ccbot',
        'amazonbot',
        'gptbot',
        'chatgpt-user',
        'oai-searchbot',
        'claudebot',
        'anthropic-ai',
        'perplexitybot'
    ];

    private map: maplibregl.Map | null = null;
    private initialBounds: maplibregl.LngLatBounds | null = null;
    private selectedMarker: maplibregl.Marker | null = null;
    private fetchDebounceTimer: ReturnType<typeof setTimeout> | null = null;
    private fetchRequestSeq = 0;
    private districtGeoJsonData: AreaGeoJSON | null = null;
    private cityGeoJsonData: AreaGeoJSON | null = null;
    /** Cached ZIP code GeoJSON — loaded on first ZIP search and reused thereafter. */
    private zipGeoJsonData: AreaGeoJSON | null = null;

    // Source/layer IDs used in MapLibre
    private readonly DISTRICTS_SOURCE = 'districts';
    private readonly DISTRICTS_FILL_LAYER = 'districts-fill';
    private readonly DISTRICTS_LINE_LAYER = 'districts-line';
    private readonly PLACE_SOURCE = 'place-boundary';
    private readonly PLACE_FILL_LAYER = 'place-boundary-fill';
    private readonly PLACE_LINE_LAYER = 'place-boundary-line';

    // Popup for district tooltip
    private popup: maplibregl.Popup | null = null;

    private autoCompleteRequest: google.maps.places.AutocompleteRequest = {
        input: '',
        includedRegionCodes: ['us'],
        // Restrict suggestions to the place kinds this component knows how to handle.
        // Max 5 entries; mixing of "primary" types and the grouping aliases isn't allowed,
        // so pick one approach:
        includedPrimaryTypes: [
            'street_address',              // house-number addresses
            'route',                       // named roads / county roads
            'locality',                    // cities & villages
            'administrative_area_level_3', // townships
            'postal_code'                  // ZIP codes
            // Note: Google Places API (New) enforces a maximum of 5 types.
            // 'premise', 'administrative_area_level_2', 'sublocality' are
            // handled via the Geocoding API fallback (Search button / Enter).
        ],
        locationRestriction: {
            north: 45.807,
            south: 41.696,
            east: -82.298636,
            west: -86.803557
        }
    }; 
 
    // Bounding box for the Upper Peninsula used as a second locationRestriction.
    // The lower peninsula rectangle is already defined in autoCompleteRequest.
    private readonly upperMichiganRestriction = {
        north: 47.45,
        south: 45.093192,
        east: -82.298636,
        west: -90.418707
    };

    constructor() {
        super();
        this.suggestions = [];
        this.selectedSenator = null;
        this.selectedSenators = [];
        this.isPanelOpen = false;
        this.activeSuggestionIndex = -1;
    }

    override connectedCallback(): void {
        super.connectedCallback();
    }

    override disconnectedCallback(): void {
        super.disconnectedCallback();
        if (this.map) {
            this.map.remove();
            this.map = null;
        }
    }

    override async firstUpdated(_changedProperties: PropertyValues): Promise<void> {
        super.firstUpdated(_changedProperties);
        // Wait for the render to complete so @query elements are available
        await this.updateComplete;
        this.loadGoogleMapsAPI();
        // Create the initial session token. Subsequent tokens are created after
        // each suggestion is picked (i.e. after Place.fetchFields ends the session).
        void this.refreshToken();
        await this.initializeMap();
        await this.applyUrlParameters();
    }

    /**
     * Reads address/lat/lng/placeId query parameters passed from the homepage
     * and performs the search automatically, avoiding a redundant Google API call
     * when coordinates are already available.
     */
    private async applyUrlParameters(): Promise<void> {
        const params = new URLSearchParams(window.location.search);
        const address = params.get('address');
        if (!address) return;

        this.isMapLoading = true;

        const t = params.get('t');
        this.searchValue = address;
        this.isSearchDisabled = true;

        const latStr = params.get('lat');
        const lngStr = params.get('lng');

        if (t === 'p' && latStr && lngStr) {
            // Homepage already geocoded the place — use coordinates directly.
            const lat = parseFloat(latStr);
            const lng = parseFloat(lngStr);
            if (!isNaN(lat) && !isNaN(lng)) {
                const location: PlaceLocation = {
                    lat,
                    lng,
                    formattedAddress: address,
                    isCityOrTownship: false,
                    isZipCode: false,
                    placeTypes: []
                };
                await this.handleLocationSelected(location);
                this.isMapLoading = false;
                return;
            }
        }

        // Fallback: geocode the address string via the Geocoding API.
        if (!this.canInitializeGoogleMapsApi()) {
            this.isMapLoading = false;
            return;
        }

        try {
            await this.handleSearchSubmit();
        } finally {
            this.isMapLoading = false;
        }
    }

    private isLegitimateCompanyBotTraffic(): boolean {
        const userAgent = globalThis.navigator?.userAgent?.toLowerCase();
        if (!userAgent) {
            return false;
        }

        return this.legitimateCompanyBotUserAgents.some(signature => userAgent.includes(signature));
    }

    private canInitializeGoogleMapsApi(): boolean {
        return this.googleApiKey.trim().length > 0 && !this.isLegitimateCompanyBotTraffic();
    }

    private loadGoogleMapsAPI(): void {
        if (window.google?.maps?.places) {
            return;
        }

        if (!this.canInitializeGoogleMapsApi()) {
            if (!this.googleApiKey.trim()) {
            console.warn('address search is be disabled, missing a key');
            }
            return;
        }

        // Check if script is already loading
        if (document.querySelector('script[src*="maps.googleapis.com"]')) {
            return;
        }

        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${this.googleApiKey}&libraries=places&callback=initGoogleMapsCallback&loading=async`;
        script.async = true;
        script.defer = true;

        window.initGoogleMapsCallback = () => {
            delete window.initGoogleMapsCallback;
        };

        document.head.appendChild(script);
    }

    private async initializeMap(): Promise<void> {
        const container = this.renderRoot.querySelector<HTMLDivElement>('#map-container');
        if (!container) {
            console.error('Map container not found in shadow DOM');
            return;
        }

        this.map = new maplibregl.Map({
            container,
            style: {
                version: 8,
                sources: {
                    'osm': {
                        type: 'raster',
                        tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
                        tileSize: 256,
                        attribution: '&copy; <a class="libraryAttribution" target="_blank" href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    }
                },
                layers: [{ id: 'osm-tiles', type: 'raster', source: 'osm' }]
            },
            center: [-85.6024, 44.3148],
            zoom: 5.5,
            cooperativeGestures: true,
            attributionControl: {
                compact: true,
                customAttribution: '<a href="https://maplibre.org/" class="libraryAttribution" target="_blank">MapLibre</a>'
            }
        });

        this.popup = new maplibregl.Popup({
            closeButton: false,
            closeOnClick: false,
            className: 'district-tooltip'
        });

        await new Promise<void>(resolve => this.map!.once('load', resolve));

        await this.loadDistrictBoundaries();
        await this.loadCityBoundaries();
    }

    private async loadDistrictBoundaries(): Promise<void> {
        try {
            this.isLoading = true;
            this.districtGeoJsonData = await FindYourSenatorDataService.getDistrictsGeoJSON();

            if (!this.map) return;

            this.map.addSource(this.DISTRICTS_SOURCE, {
                type: 'geojson',
                data: this.districtGeoJsonData as GeoJSON.FeatureCollection,
                promoteId: 'Name'
            });

            // Fill layer — uses feature-state for hover/selected/multi-color
            this.map.addLayer({
                id: this.DISTRICTS_FILL_LAYER,
                type: 'fill',
                source: this.DISTRICTS_SOURCE,
                paint: {
                    'fill-color': [
                        'case',
                        ['boolean', ['feature-state', 'selected'], false], '#D83933',
                        ['boolean', ['feature-state', 'hovered'], false], '#005ea2',
                        ['!=', ['feature-state', 'multiColor'], null],
                            ['coalesce', ['feature-state', 'multiColor'], '#1A4480'],
                        '#1A4480'
                    ],
                    'fill-opacity': [
                        'case',
                        ['boolean', ['feature-state', 'selected'], false], 0.2,
                        ['boolean', ['feature-state', 'hovered'], false], 0.2,
                        0.3
                    ]
                }
            });

            // Outline layer
            this.map.addLayer({
                id: this.DISTRICTS_LINE_LAYER,
                type: 'line',
                source: this.DISTRICTS_SOURCE,
                paint: {
                    'line-color': [
                        'case',
                        ['boolean', ['feature-state', 'selected'], false], '#d83933',
                        ['boolean', ['feature-state', 'hovered'], false], '#005ea2',
                        ['!=', ['feature-state', 'multiColor'], null], '#000000',
                        '#1a4480'
                    ],
                    'line-width': [
                        'case',
                        ['boolean', ['feature-state', 'selected'], false], 3,
                        ['boolean', ['feature-state', 'hovered'], false], 3,
                        2
                    ],
                    'line-opacity': [
                        'case',
                        ['boolean', ['feature-state', 'hovered'], false], 1,
                        0.8
                    ]
                }
            });

            this.attachDistrictEvents();
            this.attachMoveListener();

            // Fit map to district bounds
            const bounds = new maplibregl.LngLatBounds();
            (this.districtGeoJsonData as GeoJSON.FeatureCollection).features.forEach(f => {
                const geom = f.geometry as GeoJSON.Polygon | GeoJSON.MultiPolygon;
                const rings = geom.type === 'Polygon' ? [geom.coordinates] : geom.coordinates;
                rings.forEach(ring => ring[0].forEach(([lng, lat]) => bounds.extend([lng, lat])));
            });
            this.initialBounds = bounds;
            this.map.fitBounds(bounds, { padding: 20 });

            // SC 2.1.1: Populate sorted district names for the keyboard-accessible select fallback.
            this.districtNames = (this.districtGeoJsonData as GeoJSON.FeatureCollection).features
                .map(f => f.properties?.Name as string)
                .sort((a, b) => parseInt(a, 10) - parseInt(b, 10));

        } catch (error) {
            this.errorMessage = 'Failed to load district boundaries. Please try again later.';
            console.error('Error loading districts:', error);
        } finally {
            this.isLoading = false;
        }
    }

    private async loadCityBoundaries(): Promise<void> {
        try {
            this.cityGeoJsonData = await FindYourSenatorDataService.getCitiesGeoJSON();
        } catch (error) {
            // City data is only required for the multi-district city search feature.
            // A failure here should not break the rest of the map experience.
            console.error('Error loading cities:', error);
        }
    }

    /**
     * Returns the ZIP code GeoJSON, fetching it on first use and caching it for
     * subsequent searches. The ~6 MB file is intentionally not loaded at startup.
     */
    private async getZipGeoJsonData(): Promise<AreaGeoJSON | null> {
        if (this.zipGeoJsonData) {
            return this.zipGeoJsonData;
        }
        try {
            this.zipGeoJsonData = await FindYourSenatorDataService.getZipGeoJSON();
            return this.zipGeoJsonData;
        } catch (error) {
            console.error('Error loading ZIP code boundaries:', error);
            return null;
        }
    }

    private attachMoveListener(): void {
        if (!this.map) return;
        // movestart fires for any user-initiated pan or zoom
        this.map.on('movestart', (e) => {
            if ((e as unknown as { originalEvent?: Event }).originalEvent) {             
                this.isMapMoved = true;
            }
        });
    }

    private resetMapView(): void {
        if (!this.map || !this.initialBounds) return;
        this.map.fitBounds(this.initialBounds, { padding: 20, bearing: 0, pitch: 0 });
        this.isMapMoved = false;
    }

    private attachDistrictEvents(): void {
        if (!this.map) return;
        const map = this.map;
        let hoveredDistrictId: string | null = null;

        map.on('mousemove', this.DISTRICTS_FILL_LAYER, (e) => {
            const feature = e.features?.[0];
            if (!feature) return;
            const districtName = feature.properties?.Name as string;
            map.getCanvas().style.cursor = 'pointer';

            if (hoveredDistrictId && hoveredDistrictId !== districtName) {
                map.setFeatureState(
                    { source: this.DISTRICTS_SOURCE, id: hoveredDistrictId },
                    { hovered: false }
                );
            }
            hoveredDistrictId = districtName;
            // Only apply hover style if not already selected
            if (districtName !== this.selectedDistrict) {
                map.setFeatureState(
                    { source: this.DISTRICTS_SOURCE, id: districtName },
                    { hovered: true }
                );
            }

            // Show tooltip
            const districtNum = districtName.replace(/^0+/, '');
            this.popup!
                .setLngLat(e.lngLat)
                .setHTML(`District ${districtNum}`)
                .addTo(map);
        });

        map.on('mouseleave', this.DISTRICTS_FILL_LAYER, () => {
            map.getCanvas().style.cursor = '';
            if (hoveredDistrictId) {
                map.setFeatureState(
                    { source: this.DISTRICTS_SOURCE, id: hoveredDistrictId },
                    { hovered: false }
                );
                hoveredDistrictId = null;
            }
            this.popup!.remove();
        });

        map.on('click', this.DISTRICTS_FILL_LAYER, (e) => {
            const feature = e.features?.[0];
            if (!feature) return;
            const districtName = feature.properties?.Name as string;
            this.clearMultiSenatorMode();
            void this.selectDistrict(districtName, e.lngLat);
        });
    }

    private async selectDistrict(districtNumber: string, location?: maplibregl.LngLat): Promise<void> {
        if (!this.map) return;

        // Reset all district feature states
        this.districtGeoJsonData?.features.forEach(f => {
            this.map!.setFeatureState(
                { source: this.DISTRICTS_SOURCE, id: f.properties.Name },
                { selected: false, hovered: false, multiColor: null }
            );
        });
        this.districtColorMap.clear();

        this.selectedDistrict = districtNumber;
        this.map.setFeatureState(
            { source: this.DISTRICTS_SOURCE, id: districtNumber },
            { selected: true, hovered: false }
        );

        // Add/move marker if location provided
        if (location) {
            if (this.selectedMarker) {
                this.selectedMarker.setLngLat(location);
            } else {
                const el = document.createElement('div');
                el.style.cssText = 'background:#d83933;width:12px;height:12px;border-radius:50%;border:2px solid white;box-shadow:0 2px 4px rgba(0,0,0,0.3);';
                this.selectedMarker = new maplibregl.Marker({ element: el })
                    .setLngLat(location)
                    .addTo(this.map);
            }
        }

        await this.loadSenatorInfo(districtNumber);
    }

    private async loadSenatorInfo(districtNumber: string): Promise<void> {
        try {
            this.isLoading = true;
            this.selectedSenator = await FindYourSenatorDataService.getSenatorByDistrict(districtNumber);

            if (!this.selectedSenator) {
                this.errorMessage = `No senator information found for District ${districtNumber.replace(/^0+/, '')}`;
            } else {
                this.errorMessage = '';
                this.isPanelOpen = true;
                // SC 2.4.3 / SC 2.4.11: Move focus into the panel so keyboard and
                // screen reader users are not left behind the panel overlay.
                await this.updateComplete;
                this.renderRoot.querySelector<HTMLElement>('#senatorTitle')?.focus();
            }
        } catch (error) {
            this.errorMessage = 'Failed to load senator information. Please try again.';
            console.error('Error loading senator:', error);
        } finally {
            this.isLoading = false;
        }
    }

    private handleSearchInput(e: InputEvent): void {
        const input = e.target as HTMLInputElement;
        this.searchValue = input.value;
        this.isSearchDisabled = false;

        if (this.fetchDebounceTimer) {
            clearTimeout(this.fetchDebounceTimer);
            this.fetchDebounceTimer = null;
        }

        if (this.searchValue.length < 3) {
            this.suggestions = [];
            this.noMichiganResults = false;
            this.activeSuggestionIndex = -1;
            return;
        }

        this.fetchDebounceTimer = setTimeout(() => void this.fetchSuggestions(this.searchValue), 300);
    }

    private async fetchSuggestions(query: string): Promise<void> {
        const seq = ++this.fetchRequestSeq;
        try {
            if (!this.autoCompleteRequest.sessionToken) {
                await this.refreshToken();
            }
            //add in the query
            this.autoCompleteRequest.input = query;

            //Fire both peninsula requests in parallel; lower peninsula uses the
            //locationRestriction already on autoCompleteRequest, upper overrides it.
            //Since Michigan is an upside down L shape, this is a cheap way to get better coverage of the whole state without needing a more complex polygon restriction (which the API doesn't support).
            //one rectangle over the whole state includes results from Wisconsin and Illinois yuck
            const [lowerResult, upperResult] = await Promise.all([
                google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
                    this.autoCompleteRequest
                ),
                google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
                    ...this.autoCompleteRequest,
                    locationRestriction: this.upperMichiganRestriction
                })
            ]);

            // Discard if a newer request has already been dispatched.
            if (seq !== this.fetchRequestSeq) return;

            // Merge and de-duplicate by placeId, then filter to Michigan-related results.
            // Use secondaryText (e.g. "Detroit, MI, USA") rather than the full
            // display string to avoid false positives like "Michigan Ave, Chicago, IL".
            const seen = new Set<string>();
            const allPredictions = [...lowerResult.suggestions, ...upperResult.suggestions]
                .filter(s => s.placePrediction != null)
                .map(s => s.placePrediction!)
                .filter(p => {
                    if (seen.has(p.placeId)) return false;
                    seen.add(p.placeId);
                    return true;
                });

            this.suggestions = allPredictions.filter(p => {
                const secondary = p.secondaryText?.text ?? '';
                return /\bMI\b/.test(secondary) || /\bMichigan\b/i.test(secondary);
            });

            this.noMichiganResults = allPredictions.length > 0 && this.suggestions.length === 0;
            this.activeSuggestionIndex = -1;

        } catch (err) {
            if (seq !== this.fetchRequestSeq) return;
            console.error('fetchSuggestions failed', err);
            this.suggestions = [];
            this.noMichiganResults = false;
        }
    }

    // Creates a new session token for the next autocomplete session.
    // A Places API (New) session starts with the first autocomplete request and
    // ends when Place.fetchFields() is called. After that, a new token is required.
    private async refreshToken(): Promise<void> {
        if (!window.google?.maps) return;
        const { AutocompleteSessionToken } = (await google.maps.importLibrary('places')) as google.maps.PlacesLibrary;
        this.autoCompleteRequest.sessionToken = new AutocompleteSessionToken();
    }

    private async selectSuggestion(prediction: google.maps.places.PlacePrediction): Promise<void> {
        // this.searchValue = prediction.text.text;
        this.suggestions = [];
        this.noMichiganResults = false;
        this.isSearchDisabled = true;

        try {
            this.isLoading = true;
            const location = await FindYourSenatorDataService.geocodePlace(prediction);

            if (location) {
                this.searchValue = location.formattedAddress || this.searchValue;
                await this.handleLocationSelected(location);
            } else {
                this.errorMessage = 'Could not find that location. Please try a different search.';
            }
        } catch (error) {
            this.errorMessage = 'Error searching for location. Please try again.';
            console.error('Geocode error:', error);
        } finally {
            // The session ended when Place.fetchFields was called inside geocodePlace,
            // so start a fresh session token for the next autocomplete session.
            await this.refreshToken();
            this.isLoading = false;
        }
    }

    private async handleLocationSelected(location: PlaceLocation): Promise<void> {
        if (!this.map || !this.districtGeoJsonData) return;

        // Check if location is in Michigan
        if (!FindYourSenatorDataService.isPointInMichigan(location.lat, location.lng)) {
            this.errorMessage = 'That location appears to be outside of Michigan. Please search for a Michigan address.';
            return;
        }

        // Any new search resets the previous multi-senator state.
        this.clearMultiSenatorMode();

        // zoom out a bit if a city was searched for
        const zoomLevel = location.isCityOrTownship || location.isZipCode ? 10 : 14;
        this.map.flyTo({ center: [location.lng, location.lat], zoom: zoomLevel });

        // City/township search: try to match to a city polygon and find every
        // district that overlaps it. Falls back to single-district point lookup
        // if the location is in Michigan but doesn't fall inside any city polygon.
        if (location.isCityOrTownship && this.cityGeoJsonData) {
            const placeFeature = findPlaceAtPoint(location.lat, location.lng, this.cityGeoJsonData);
            if (placeFeature) {
                const districts = findDistrictsIntersectingPlace(placeFeature, this.districtGeoJsonData);
                if (districts.length > 1) {
                    await this.enterMultiSenatorMode(districts, placeFeature);
                    return;
                }
                if (districts.length === 1) {
                    this.drawPlaceBoundary(placeFeature);
                    await this.selectDistrict(districts[0], new maplibregl.LngLat(location.lng, location.lat));
                    return;
                }
            }
        }

        // ZIP code search: lazy-load the large ZIP GeoJSON on first use, then reuse the
        // cached reference. Treats the ZIP polygon the same as a city polygon.
        if (location.isZipCode) {
            const zipData = await this.getZipGeoJsonData();
            if (zipData) {
                const zipFeature = findPlaceAtPoint(location.lat, location.lng, zipData);
                if (zipFeature) {
                    const districts = findDistrictsIntersectingPlace(zipFeature, this.districtGeoJsonData);
                    if (districts.length > 1) {
                        await this.enterMultiSenatorMode(districts, zipFeature);
                        return;
                    }
                    if (districts.length === 1) {
                        this.drawPlaceBoundary(zipFeature);
                        await this.selectDistrict(districts[0], new maplibregl.LngLat(location.lng, location.lat));
                        return;
                    }
                }
            }
        }

        // Single-district fallback (street address, or city/ZIP with no polygon match).
        const districtNumber = findDistrictAtPoint(location.lat, location.lng, this.districtGeoJsonData);

        if (districtNumber) {
            await this.selectDistrict(districtNumber, new maplibregl.LngLat(location.lng, location.lat));
        } else {
            this.errorMessage = 'Could not determine the senate district for that location.';
        }
    }

    /**
     * Switches the panel into the multi-senator list view, highlights every
     * intersecting district in red, and draws the city boundary outline.
     */
    private async enterMultiSenatorMode(districtNumbers: string[], placeFeature: AreaFeature): Promise<void> {
        // Reset prior single-district selection state.
        this.selectedDistrict = null;
        this.selectedSenator = null;
        this.removeSelectedMarker();

        // Highlight every matched district in the red selected style.
        this.multiSelectedDistricts = districtNumbers;
        this.applyMultiSelectionStyles();

        this.drawPlaceBoundary(placeFeature);

        this.multiSenatorPlace = placeFeature.properties.Name;

        try {
            this.isLoading = true;
            this.selectedSenators = await FindYourSenatorDataService.getSenatorsByDistricts(districtNumbers);
            this.errorMessage = '';
            if (!this.isPanelOpen) {
                this.isPanelOpen = true;
            }
            // SC 2.4.3 / SC 2.4.11: Move focus into the panel after content renders.
            await this.updateComplete;
            this.renderRoot.querySelector<HTMLElement>('#senatorTitle')?.focus();
        } catch (error) {
            this.errorMessage = 'Failed to load senator information. Please try again.';
            console.error('Error loading senators:', error);
        } finally {
            this.isLoading = false;
        }
    }

    /** Re-applies distinct colorblind-safe fill colors to every district in the multi-selection. */
    private applyMultiSelectionStyles(): void {
        if (!this.map || !this.districtGeoJsonData) return;


        // Map each district name to its stable color index.
        this.districtColorMap = new Map(
            this.multiSelectedDistricts.map((name, i) => [
                name,
                this.multiSelectionColors[i % this.multiSelectionColors.length]
            ])
        ); 

        // Reset all districts to default feature state
        this.districtGeoJsonData.features.forEach(f => {
            this.map!.setFeatureState(
                { source: this.DISTRICTS_SOURCE, id: f.properties.Name },
                { selected: false, hovered: false, multiColor: null }
            );
        });

        // Apply multi-colors to matched districts
        this.multiSelectedDistricts.forEach(name => {
            const fillColor = this.districtColorMap.get(name)!;
            this.map!.setFeatureState(
                { source: this.DISTRICTS_SOURCE, id: name },
                { multiColor: fillColor }
            );
        });
    }

    /** Draws the city/township polygon as a thin dashed outline beneath the district layers. */
    private drawPlaceBoundary(placeFeature: AreaFeature): void {
        if (!this.map) return;
        this.removePlaceBoundary();

        this.map.addSource(this.PLACE_SOURCE, {
            type: 'geojson',
            data: placeFeature as unknown as GeoJSON.Feature
        });

        this.map.addLayer({
            id: this.PLACE_FILL_LAYER,
            type: 'fill',
            source: this.PLACE_SOURCE,
            paint: {
                'fill-color': '#D83933',
                'fill-opacity': 0.16
            }
        }); // above district layers

        this.map.addLayer({
            id: this.PLACE_LINE_LAYER,
            type: 'line',
            source: this.PLACE_SOURCE,
            paint: {
                'line-color': '#ffffff',
                'line-width': 4,
                'line-opacity': 1
            }
        }); // above district layers
    }

    private removePlaceBoundary(): void {
        if (!this.map) return;
        for (const id of [this.PLACE_FILL_LAYER, this.PLACE_LINE_LAYER]) {
            if (this.map.getLayer(id)) this.map.removeLayer(id);
        }
        if (this.map.getSource(this.PLACE_SOURCE)) this.map.removeSource(this.PLACE_SOURCE);
    }

    private removeSelectedMarker(): void {
        if (this.selectedMarker) {
            this.selectedMarker.remove();
        }
        this.selectedMarker = null;
    }

    /** Clears multi-senator state, removes the city outline, and resets district styles. */
    private clearMultiSenatorMode(): void {
        if (this.multiSelectedDistricts.length === 0 && !this.map?.getSource(this.PLACE_SOURCE) && this.selectedSenators.length === 0) {
            return;
        }
        this.multiSelectedDistricts = [];
        this.selectedSenators = [];
        this.multiSenatorPlace = null;
        this.removePlaceBoundary();
        // Reset all district feature states to default
        this.districtGeoJsonData?.features.forEach(f => {
            this.map!.setFeatureState(
                { source: this.DISTRICTS_SOURCE, id: f.properties.Name },
                { selected: false, hovered: false, multiColor: null }
            );
        });
        this.districtColorMap.clear();
    }

    /** Drill into a single senator from the multi-senator list. */
    private async showSenatorFromList(senator: SenatorInfo): Promise<void> {
        this.selectedSenator = senator;
        const paddedDistrict = senator.district.padStart(2, '0');
        this.selectedDistrict = paddedDistrict;
        // Restore the multi-selection coloring, then override the chosen district
        this.applyMultiSelectionStyles();
        if (this.map) {
            this.map.setFeatureState(
                { source: this.DISTRICTS_SOURCE, id: paddedDistrict },
                { selected: true, multiColor: null }
            );
        }

        if (!this.isPanelOpen) {
            this.isPanelOpen = true;
        }
        // SC 2.4.3 / SC 2.4.11: Move focus to the panel title after content renders.
        await this.updateComplete;
        this.renderRoot.querySelector<HTMLElement>('#senatorTitle')?.focus();
    }

    private handleSenatorListMouseEnter (senator: SenatorInfo): void {
        if (!this.map) return;
        const paddedDistrict = senator.district.padStart(2, '0');
        this.map.setFeatureState(
            { source: this.DISTRICTS_SOURCE, id: paddedDistrict },
            { hovered: true }
        );
    }

    private handleSenatorListMouseLeave(senator: SenatorInfo): void {
        if (!this.map) return;
        const paddedDistrict = senator.district.padStart(2, '0');
        this.map.setFeatureState(
            { source: this.DISTRICTS_SOURCE, id: paddedDistrict },
            { hovered: false }
        );
    }

    /** Return from a drill-down back to the multi-senator list. */
    private backToSenatorList(): void {
        this.selectedSenator = null;
        this.selectedDistrict = null;
        this.applyMultiSelectionStyles();
    }

    private openPanel(): void {
        this.isPanelOpen = true;
    }

    private dismissPanel(): void {
        this.isPanelOpen = false;
    }

    private clearSearch(): void {
        this.searchValue = '';
        this.suggestions = [];
        this.noMichiganResults = false;
        this.activeSuggestionIndex = -1;
        this.renderRoot.querySelector<HTMLInputElement>('#search-input')?.focus();

        // Close the panel
        this.dismissPanel();

        // Clear the single-district feature state on the map before resetting the reference
        if (this.selectedDistrict && this.map) {
            this.map.setFeatureState(
                { source: this.DISTRICTS_SOURCE, id: this.selectedDistrict },
                { selected: false, hovered: false }
            );
        }

        // Reset prior single-district selection state.
        this.selectedDistrict = null;
        this.selectedSenator = null;
        this.removeSelectedMarker();
        this.clearMultiSenatorMode();
        this.removePlaceBoundary();
        this.resetMapView();
    }

    private async handleSearchSubmit(): Promise<void> {
        const query = this.searchValue.trim();
        if (!query) return;

        this.suggestions = [];
        this.activeSuggestionIndex = -1;

        try {
            this.isLoading = true;
            this.errorMessage = '';
            const location = await FindYourSenatorDataService.geocodeAddress(query);
            if (location) {
                this.searchValue = location.formattedAddress || query;
                await this.handleLocationSelected(location);
            } else {
                this.errorMessage = 'Could not find that location. Please try a different search.';
                this.clearSearch();
            }
        } catch (error) {
            this.errorMessage = 'Error searching for location. Please try again.';
            console.error('Geocode error:', error);
        } finally {
            this.isLoading = false;
        }
    }

    private handleSearchKeydown(e: KeyboardEvent): void {
        if (e.key === 'Escape') {
            this.suggestions = [];
            this.activeSuggestionIndex = -1;
            return;
        }

        if (e.key === 'Enter') {
            if (this.activeSuggestionIndex >= 0 && this.suggestions.length > 0) {
                e.preventDefault();
                void this.selectSuggestion(this.suggestions[this.activeSuggestionIndex]);
            } else if (this.suggestions.length === 0) {
                e.preventDefault();
                void this.handleSearchSubmit();
            }
            return;
        }

        if (this.suggestions.length === 0) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            this.activeSuggestionIndex = Math.min(this.activeSuggestionIndex + 1, this.suggestions.length - 1);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            this.activeSuggestionIndex = Math.max(this.activeSuggestionIndex - 1, -1);
        }
    }

    //private handleSearchBlur(): void {
    //    // Delay to allow click on suggestion, then only hide if focus left the component
    //    //setTimeout(() => {
    //    //    const active = this.shadowRoot?.activeElement;
    //    //    if (!active || !active.closest('.suggestions-dropdown')) {
    //    //        this.showSuggestions = false;
    //    //    }
    //    //}, 200);
    //}

    private senatorPhotoUrl(senator: SenatorInfo): string {
        return senator.imageUrl ? `https://senate.michigan.gov${senator.imageUrl}` : PHOTO_PLACEHOLDER_PATH;
    }

    private usePhotoPlaceholder(event: Event): void {
        const image: HTMLImageElement = event.target as HTMLImageElement;
        if (!image.src.endsWith(PHOTO_PLACEHOLDER_PATH)) {
            image.src = PHOTO_PLACEHOLDER_PATH;
        }
    }

    private formatSenatorName(senator: SenatorInfo): string {
        let name = senator.firstName;
        if (senator.middleInitial) {
            name += ` ${senator.middleInitial}`;
        }
        name += ` ${senator.lastName}`;
        if (senator.suffix) {
            name += `, ${senator.suffix}`;
        }
        return name;
    }

    override render() {
        return html`
       
            <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.7.2/css/all.min.css">
            <link rel="stylesheet" href="https://unpkg.com/maplibre-gl/dist/maplibre-gl.css" />
            <link rel="stylesheet" href="/css/all.css" />

         
            <div class="fys-container">        

                <div ?hidden="${this.errorMessage === ''}" class="error-message" role="alert">
                    ${this.errorMessage}
                </div>

                
                <div class="search-container">
                    <div id="mainSearch">
                        <label for="search-input" class="sr-only">Search for an address, city, or place in Michigan</label>
                        <div class="search-input-wrapper" id="fysMainSearch">
                            <input       
                                id="search-input"
                                data-testid="fys-map-search-input"
                                type="text"                                 
                                placeholder="Enter an address, city, or place in Michigan..."
                                role="combobox"
                                aria-expanded=${this.suggestions.length > 0}
                                aria-controls="suggestions-listbox"
                                aria-activedescendant=${ifDefined(this.activeSuggestionIndex >= 0 ? `suggestion-${this.activeSuggestionIndex}` : undefined)}
                                aria-autocomplete="list"
                                .value=${this.searchValue}
                                @input=${this.handleSearchInput}
                                @keydown=${this.handleSearchKeydown}                      
                                ?disabled=${!this.canInitializeGoogleMapsApi()}
                                autocomplete="off"
                            />
                            ${this.searchValue ? html`
                                <button
                                    type="button"
                                    class="search-clear-btn"
                                    aria-label="Clear search"
                                    @click=${this.clearSearch}
                                >&#x2715;</button>
                            ` : ''}
                        </div>
                        <div>
                        <button
                            type="button"
                            class="search-submit-btn btn"
                            aria-label="Search"
                            ?disabled=${!this.searchValue.trim() || this.isLoading || this.isSearchDisabled}
                            @click=${this.handleSearchSubmit}
                        >Search</button>
                        </div>
                    </div>

                    ${this.suggestions.length > 0 ? html`
                        <div class="suggestions-container">
                            <div class="suggestions-dropdown" id="suggestions-listbox" role="listbox" aria-label="Location suggestions">
                                ${this.suggestions.map((suggestion, index) => html`
                                    <div 
                                                id="suggestion-${index}"
                                                class="suggestion-item ${index === this.activeSuggestionIndex ? 'suggestion-active' : ''}"
                                                role="option"
                                                aria-selected=${index === this.activeSuggestionIndex}
                                                @click=${() => this.selectSuggestion(suggestion)}
                                            >
                                                <div class="suggestion-main">${suggestion.mainText?.text}</div>
                                                <div class="suggestion-secondary">${suggestion.secondaryText?.text}</div>
                                            </div>
                                `)}
                            </div>
                            <div class="suggestions-close-row">
                                <button class="suggestions-close-btn" @click=${() => { this.suggestions = []; }} aria-label="Close suggestions">Close</button>
                            </div>
                        </div>
                    ` : this.noMichiganResults ? html`
                        <div class="suggestions-container">
                            <div class="suggestions-dropdown" role="status" aria-live="polite">
                                <div class="suggestion-item suggestion-no-michigan">
                                    <div class="suggestion-main">No results found in Michigan</div>
                                    <div class="suggestion-secondary">Only results in Michigan are displayed</div>
                                </div>
                            </div>
                            <div class="suggestions-close-row">
                                <button class="suggestions-close-btn" @click=${() => { this.noMichiganResults = false; }} aria-label="Close suggestions">Close</button>
                            </div>
                        </div>
                    ` : ''}

                </div>

                <div class="map-and-info">
                    <div class="map-wrapper">
                        <div id="map-container" role="application" aria-label="Interactive map of Michigan senate districts. Use the search above or the district selector below to view Senator information."></div>
                        <label class="sr-only" for="district-select">Select a senate district by number</label>
                        <select
                            id="district-select"
                            data-testid="fys-district-select"
                            class="sr-only"
                            @change=${(e: Event) => {
                                const val = (e.target as HTMLSelectElement).value;
                                if (val) {
                                    this.clearMultiSenatorMode();
                                    void this.selectDistrict(val);
                                }
                                (e.target as HTMLSelectElement).value = '';
                            }}
                        >
                            <option value="">-- Select a district --</option>
                            ${this.districtNames.map(name => html`
                                <option value="${name}">District ${name.replace(/^0+/, '')}</option>
                            `)}
                        </select>
                        ${this.isMapLoading ? html`
                            <div class="map-loading-overlay" role="status" aria-live="polite">
                                <span class="loading-spinner"></span>
                                <p>Loading your location&hellip;</p>
                            </div>
                        ` : ''}
                        ${this.isMapMoved ? html`
                            <button
                                title="Reset map to initial view"
                                type="button"
                                class="map-reset-btn"
                                aria-label="Reset map to initial view"
                                @click=${this.resetMapView}
                            >&#8635; Reset</button>
                        ` : ''}
                    </div>

                    <div class="senator-panel ${this.isPanelOpen ? 'panel-open' : ''}" role="region" aria-label="Senator information">
                        <button class="panel-tab" @click=${this.isPanelOpen ? this.dismissPanel : this.openPanel} aria-label="${this.isPanelOpen ? 'Close' : 'Open'} senator panel">
                            <span class="panel-tab-label">${this.isPanelOpen ? 'Close' : 'Open'}</span>
                            <span class="panel-tab-arrow">${this.isPanelOpen ? html`&#9654;` : html`&#9664;`}</span>
                        </button>
                        <div class="senator-panel-scroll">
                            ${this.renderSenatorInfo()}
                        </div>
                    </div>
                </div>
            </div>

                `;
    }

    private renderSenatorInfo() {
        const senator = this.selectedSenator;
        const party = senator?.party?.toLowerCase() ?? '';
        const partyClass =
            party === 'd' ? 'party-democrat' :
                party === 'r' ? 'party-republican' :
                    party === 'i' ? 'party-independent' :
                        'party-none';
        const partyFullName =
            party === 'd' ? 'Democrat' :
                party === 'r' ? 'Republican' :
                    party === 'i' ? 'Independent' :
                        'None';

        // Multi-senator list view: shown when a city search matched 2+ districts
        // and the user has not yet drilled into an individual senator.
        const showMultiList = !senator && this.selectedSenators.length > 1;
        const showBackButton = senator && this.selectedSenators.length > 1;

        return html` 
            <link rel='stylesheet' href='/css/all.css'>
            <div class="panel-header">
                ${senator && !this.isLoading
                ? html`<h2 id="senatorTitle" data-testid="fys-senator-title" tabindex="-1">Senator ${this.formatSenatorName(senator)}</h2>`
                : showMultiList && !this.isLoading
                    ? html`<h2 id="senatorTitle" data-testid="fys-senator-title" tabindex="-1">Senators for ${this.multiSenatorPlace ?? 'this area'}</h2>` : html`<h2 id="senatorTitle" data-testid="fys-senator-title" tabindex="-1">Find Your Senator</h2>`}
            </div>
            <button class="mobile-close-btn" @click=${this.dismissPanel} aria-label="Close senator panel">&#10005; Close</button>
            ${this.isLoading ? html`
                <div class="placeholder-message">
                    <span class="loading-spinner"></span>
                    <p>Loading...</p>
                </div>
            ` : showMultiList ? this.renderSenatorList() : !senator ? html`
                <div class="panel-instructions">                   
                    <p>Enter an address, city, or ZIP code in the search box above, or click directly on the map to see which Senator represents that area.</p>
                </div>
            ` : html`
            <div class="panel-content">
                ${showBackButton ? html`
                    <button type="button" class="back-to-list-btn" @click=${this.backToSenatorList}>
                        &#8592; Back
                    </button>
                ` : ''}
            <div class="senator-card">            
                 <div class="leader-photo">
                      <a target="_blank" rel="noopener noreferrer" href="${senator.website}"
                         aria-label="View ${this.formatSenatorName(senator)}'s official website (opens in new tab)">
                        <img class="circular-photo" src="${this.senatorPhotoUrl(senator)}" alt="" aria-hidden="true" @error=${this.usePhotoPlaceholder}>
                      </a>
                  </div>                
                
                <span class="senator-party ${partyClass}">${partyFullName}</span>
           
                <div class="senator-district">District ${senator.district}</div>

                <div class="senator-contact">

                  ${senator.website ? html`
                        <div class="contact-item contact-website">
                            <span class="contact-label"><i class="fa-solid fa-globe" aria-hidden="true"></i></span>
                            <a href="${senator.website}" target="_blank" rel="noopener noreferrer" class="contact-link">
                                ${senator.website}<span class="sr-only"> (opens in new tab)</span>
                            </a>
                        </div>
                    ` : ''}

                    ${senator.phone ? html`
                        <div class="contact-item">
                            <span class="contact-label"><i class="fa-solid fa-phone-volume" aria-hidden="true"></i></span>
                            <a href="tel:${senator.phone}" class="contact-link"
                               aria-label="Call ${this.formatSenatorName(senator)} at ${senator.phone}">
                                ${senator.phone}
                            </a>
                        </div>
                    ` : ''}

                    ${senator.email ? html`
                        <div class="contact-item">
                            <span class="contact-label"><i class="fa-solid fa-envelope" aria-hidden="true"></i></span>
                            <a href="mailto:${senator.email}" class="contact-link"
                               aria-label="Email ${this.formatSenatorName(senator)} at ${senator.email}">
                                ${senator.email}
                            </a>
                        </div>
                    ` : ''}
                </div>

                ${senator.website ? html`
                    <a href="${senator.website}" class="view-page-btn" target="_blank" rel="noopener noreferrer"
                       aria-label="View ${this.formatSenatorName(senator)}'s website (opens in new tab)">
                        <i id="globeIcon" class="fa-solid fa-globe" aria-hidden="true"></i> View Senator's Website
                    </a>
                ` : senator.contactUrl ? html`
                    <a href="${senator.contactUrl}" class="view-page-btn">Contact Senator</a>
                ` : ''}
            </div>
              </div>
            `}
        `;
    }

    private renderSenatorList() {
        const cityName = this.multiSenatorPlace ?? 'this area';
        return html`
            <div class="panel-content">
                <p class="multi-senator-message">
                    Multiple Senators represent <strong>${cityName}</strong>. Select a Senator below for details.
                </p>
                <ul class="senator-list" role="list">
                    ${this.selectedSenators.map(s => html`
                        <li @mouseenter=${() => this.handleSenatorListMouseEnter(s)} @mouseleave=${() => this.handleSenatorListMouseLeave(s)}>
                            <button type="button" class="senator-list-item" @click=${() => this.showSenatorFromList(s)}>
                                <img class="senator-list-photo" src="${this.senatorPhotoUrl(s)}" alt="Headshot of ${this.formatSenatorName(s)}" @error=${this.usePhotoPlaceholder}>
                                <div class="senator-list-info">
                                    <div class="senator-list-name">${this.formatSenatorName(s)}</div>
                                    <div class="senator-list-district">District ${s.district.replace(/^0+/, '')}</div>
                                     
                                </div>
                            </button>
                        </li>
                    `)}
                </ul>
            </div>
        `;
    }

    static override styles = [
        unsafeCSS(findYourSenatorStyles),
        unsafeCSS(SearchStyles),
        css`
        :host {
            display: block;
            font-family: var(--font-family)
        }

        .maplibregl-map {
            font-family: var(--font-family) !important;
        }

        .maplibregl-ctrl-attrib-inner{
            color:rgba(0,0,0,.75);
        }

        #senatorTitle{
            font-size:22px;
            color:#fff;
            font-family: var(--font-family, effra, sans-serif);
            font-weight: var(--font-weight, 400);
            font-style: var(--font-style, normal);
            margin: 0;
            outline: none;
        }

        /* SC 2.4.13: Provide a visible focus ring on the map canvas instead of removing it. */
        .maplibregl-canvas:focus-visible {
            outline: 3px solid #005ea2;
            outline-offset: 2px;
        }

        .maplibregl-ctrl-zoom-in, .maplibregl-ctrl-zoom-out {
            text-decoration: none;
        }

        #fysMainSearch{
            display:flex;
            gap:0.5rem;
            width:100%
        }

        .fys-container {
            max-width: 1200px;
            margin: 0 auto;
            position:relative;
            z-index:500
        }

        .search-container {
            margin-bottom: 1rem;
            position: relative;
        }       

       #mainSearch{
         display: flex;
         gap: 10px;
       }

      input, button {
           font-family:var(--font-family, effra, sans-serif););
           font-size:16px;
       }

        .search-input-field {
            position: relative;
            flex: 1;
        }         

        .search-clear-btn {
            position: absolute;
            right: 0.5rem;
            top: 50%;
            transform: translateY(-50%);
            background: none;
            border: none;
            cursor: pointer;
            color: #d83933;
            font-size: 1.1rem;
            line-height: 1;
            padding: 0.5rem;
            min-width: 44px;
            min-height: 44px;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .search-clear-btn:hover {
            color: #9b1c1c;
        }

        .search-submit-btn {         
            background-color: #1a4480; 
            height:100%;
            min-width:unset;            
        }

        .search-submit-btn:hover:not(:disabled) {
            background-color: #005ea2;
        }

        .search-submit-btn:disabled {
            background-color: #71767a;
            cursor: not-allowed;
        }
         .suggestions-container {
             position: absolute;
             top: 100%;
             left: 0;
             right: 0;
             z-index: 2000;
         }

          .suggestions-dropdown {
             background: white;
             border: 1px solid #ddd;
             border-radius: 4px 4px 0 0;
             box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
             max-height: 300px;
             overflow-y: auto;
         }

        .suggestion-item {
            padding: 0.75rem 1rem;
            cursor: pointer;
            border-bottom: 1px solid #eee;
        }

        .suggestion-item:last-child {
            border-bottom: none;
        }

        .suggestion-item:hover,
        .suggestion-item:focus {
            background-color: #f0f0f0;
        }

        .suggestion-main {
            font-weight: 500;
        }

        .suggestion-secondary {
            font-size: 0.85rem;
            color: #666;
        }

        .suggestion-no-michigan {
            cursor: default;
            color: #71767a;
        }

        .suggestion-no-michigan:hover,
        .suggestion-no-michigan:focus {
            background-color: white;
        }

        .suggestion-no-michigan .suggestion-main {
            color: #1a4480;
        }

        .suggestions-close-row {
            display: flex;
            justify-content: flex-end;
            padding: 0.4rem 0.75rem 0.5rem;          
            
        }

        .suggestions-close-btn {
            background: #fff;
            border: 1px solid #ccc;
            border-radius: 4px;
            padding: 0.25rem 0.75rem;
            font-size: 0.85rem;
            cursor: pointer;
            color: #444;
            line-height: 1.4;
            min-height: 0;
        }

        .suggestions-close-btn:hover,
        .suggestions-close-btn:focus {
            background-color: #f0f0f0;
            border-color: #999;
        }

        .map-and-info {
            position: relative;
            overflow:hidden;
        }

        .map-wrapper {
            position: relative;
            width: 100%;
            border-radius: 8px;
            overflow: hidden;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        #map-container {
            height: 500px;
            width: 100%;
            background: #f5f5f5;
        }

        .senator-panel {
            position: absolute;
            top: 0;
            right: 0;
            width: 350px;
            height: 100%;
            overflow: visible;
            background: white;
            border-radius: 0 8px 8px 0;
            box-shadow: -2px 0 12px rgba(0, 0, 0, 0.2);
            z-index: 1000;
            transform: translateX(100%);
            transition: transform 0.3s ease;
            display: flex;
            flex-direction: column;
            min-height: 0;
        }

        .senator-panel.panel-open {
            transform: translateX(0);
        }

        .panel-header {
            flex: 0 0 auto;
        }

        .senator-panel-scroll {
            flex: 1 1 auto;
            min-height: 0;
            display: flex;
            flex-direction: column;
            border-radius: 0 0 8px 0;
        }

        .senator-panel-scroll > .panel-content {
            position:relative;
            flex: 1 1 auto;
            min-height: 0;
            display: flex;
            flex-direction: column;
        }

        .panel-tab {
            position: absolute;
            left: -36px;
            top: 50%;
            transform: translateY(-50%);
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 4px;
            width: 36px;
            padding: 10px 0;
            background: white;
            color: rgb(119, 119, 119);
            border: 1px solid #ddd;
            border-right: none;
            border-radius: 6px 0 0 6px;
            cursor: pointer;
            box-shadow: -3px 0 8px rgba(0, 0, 0, 0.1);
            transition: background 0.2s, color 0.2s;
            overflow: hidden;
            z-index: 1001;
        }

        .panel-tab:hover {
            background: #f0f0f0;
            color: #333;
        }

        .panel-tab-label {
            font-size: 0.7rem;
            font-weight: 600;
            font-family: var(--font-family, effra, sans-serif);
            letter-spacing: 0.05em;
            writing-mode: vertical-rl;
            text-orientation: mixed;
            transform: rotate(180deg);
            white-space: nowrap;

        }

        .panel-tab-arrow {
            font-size: 0.75rem;
            line-height: 1;
        }

        #globeIcon{
            color:#fff
        }

        .mobile-close-btn {
            display: none;
            align-items: center;
            justify-content: center;
            gap: 0.4rem;
            width: 100%;
            padding: 0.5rem 1rem;
            background: #b22222;
            color: white;
            border: none;
            border-bottom: 1px solid rgba(0,0,0,0.15);
            font-size: 0.95rem;
            font-weight: 600;
            cursor: pointer;
            letter-spacing: 0.03em;
            flex-shrink: 0;
        }

        .mobile-close-btn:hover {
            background: #8b0000;
        }

 
        .panel-header {
            background: #1a4480;
            color: white;
            padding: 1rem;
            display: flex;
            justify-content: space-between;
            align-items: center;
            min-height:68px;
        }

        .panel-close {
            background: none;
            border: none;
            color: white;
            font-size: 1.5rem;
            cursor: pointer;
            padding: 0 0.25rem;
            line-height: 1;
        }

        .panel-close:hover {
            opacity: 0.8;
        }

        .panel-header h3 {
            margin: 0;
            font-size: 1.1rem;
        }

        .panel-content {
            padding: 1rem;
        }

        .senator-card {
           display: flex;
           flex-direction: column;
           justify-content: center;
           align-items: center;
           gap: 10px;
        }

        .senator-image {
            width: 150px;
            height: 180px;
            object-fit: cover;
            border-radius: 8px;
            margin-bottom: 1rem;
            background: #eee;
        }

        .senator-name {
            font-size: 1.25rem;
            font-weight: 600;
            margin: 0 0 0.5rem;
            color: #1a4480;
        }

        .senator-party {
            display: inline-block;
            padding: 0.25rem 0.75rem;
            border-radius: 20px;
            font-size: 0.85rem;
            font-weight: 500;
            margin-top: 0.75rem;


        }

        .party-democrat {
            background: var(--secondary);
            color: white;
        }

        .party-republican {
            background: var(--primary);
            color: white;
        }



        .party-independent {
            background: #b8860b;
            color: white;
        }

        .party-none {
            background: #71767a;
            color: white;
        }

        .senator-district {
            color: #666; 
        }

        .senator-contact { 
            border-top: 1px solid #eee;
            padding-top: 1rem;
        }

        .contact-item {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            margin-bottom: 0.5rem;
            font-size: 0.9rem;
        }

        .contact-label {
            font-weight: 500;
    
        }

        .contact-link {
            color: #005ea2;
            text-decoration: none;
        }

        .contact-link:hover {
            text-decoration: underline;
        }

        .view-page-btn {
            display: block;
            width: 100%;
            padding: 0.75rem;           
            background: #1a4480;
            color: white;
            text-align: center;
            text-decoration: none;
            border-radius: 4px;
            font-weight: 500;
            transition: background 0.2s;
        }

        .view-page-btn:hover {
            background: #005ea2;
        }

        .placeholder-message {
            color: #666;
            text-align: center;
            padding: 2rem 1rem;
        }

        .panel-instructions {
            padding: 1.5rem 1rem;
            color: #1a4480;
        }

        .panel-instructions p {
            margin: 0 0 0.5rem;
        }

        .placeholder-message p {
            margin: 0.5rem 0;
        }

        .error-message {
            background: #fef0f0;
            border: 1px solid #d83933;
            color: #d83933;
            padding: 1rem;
            border-radius: 4px;
            margin-bottom: 1rem;
        }

        .error-message:empty {
            display: none;
        }

        .loading-spinner {
            display: inline-block;
            width: 20px;
            height: 20px;
            border: 2px solid #ccc;
            border-top-color: #1a4480;
            border-radius: 50%;
            animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
            to { transform: rotate(360deg); }
        }

        .sr-only {
            position: absolute;
            width: 1px;
            height: 1px;
            padding: 0;
            margin: -1px;
            overflow: hidden;
            clip: rect(0, 0, 0, 0);
            white-space: nowrap;
            border: 0;
        }

        .loading-status {
            position: absolute;
            width: 1px;
            height: 1px;
            padding: 0;
            margin: -1px;
            overflow: hidden;
            clip: rect(0, 0, 0, 0);
            white-space: nowrap;
            border: 0;
        }

        .suggestion-item:focus-visible {
            outline: 2px solid #005ea2;
            outline-offset: -2px;
        }

        .suggestion-active {
            background-color: #e8f0fe;
        }

        .panel-tab:focus-visible {
            outline: 2px solid #005ea2;
            outline-offset: 2px;
        }

  

        .multi-senator-message {
            margin: 0 0 1rem;
         
            font-size: 0.95rem;
        }

        .senator-list {
            list-style: none;
            margin: 0;
            padding: 0;
            flex: 1 1 auto;
            min-height: 0;
            overflow-y: auto;
        }

        .senator-list li + li {
            margin-top: 0.5rem;
        }

        .senator-list-item {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            width: 100%;
            padding: 0.5rem;
            background: white;
            border: 1px solid #ddd;
            border-radius: 6px;
            cursor: pointer;
            text-align: left;
            transition: background 0.15s, border-color 0.15s;
        }

        .senator-list-item:hover,
        .senator-list-item:focus-visible {
            background: #f0f4fa;
            border-color: #1a4480;
            outline: 3px solid #005ea2;
            outline-offset: 2px;
        }

        .senator-list-photo {
            width: 48px;
            height: 48px;
            border-radius: 50%;
            object-fit: cover;
            flex-shrink: 0;
            background: #eee;
        }

        .senator-list-photo-empty {
            border: 1px dashed #ccc;
        }

        .senator-list-info {
            display: flex;
            flex-direction: column;
            gap: 2px;
            min-width: 0;
        }

        .senator-list-name {
            font-weight: 600;
            color: #1a4480;
        }

        .senator-list-district {
            font-size: 0.8rem;
            color: #666;
        }

    

        .back-to-list-btn {
            position:absolute;
            top:10px;
            left:10px;
            padding: 0.4rem 0.75rem;
            background: white;
            color: #1a4480;
            border: 1px solid #1a4480;
            border-radius: 4px;
            font-size: 0.85rem;
            font-weight: 500;
            cursor: pointer;
            width:80px; 

        }

        .back-to-list-btn:hover,
        .back-to-list-btn:focus-visible {
            background: #1a4480;
            color: white;
            outline: 3px solid #005ea2;
            outline-offset: 3px;
        }

        .map-loading-overlay {
            position: absolute;
            inset: 0;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 0.75rem;
            background: rgba(255, 255, 255, 0.75);
            z-index: 900;
            border-radius: 8px;
            pointer-events: none;
        }

        .map-loading-overlay p {
            margin: 0;
            font-size: 1rem;
            font-weight: 500;
            color: #1a4480;
        }

        .map-reset-btn {
            position: absolute;
            bottom: 1rem;
            left: 10px;            
            z-index: 900;
            padding: 0.3rem .75rem;
            background: white;
            color: #1a4480;
            border: 2px solid #1a4480;
            border-radius: 4px;
            font-size: 0.9rem;
            font-weight: 600;
            cursor: pointer;
            box-shadow: 0 2px 6px rgba(0,0,0,0.2);
            transition: background 0.2s, color 0.2s;
        }

        .map-reset-btn:hover,
        .map-reset-btn:focus-visible {
            background: #1a4480;
            color: white;
            outline: 3px solid #ffbe2e;
            outline-offset: 3px;
        }

    .libraryAttribution{
        font-family: var(--font-family, effra, sans-serif);        
    }

      @media (max-width: 600px) {
            .senator-panel {
                width: 100%;
                border-radius: 0 0 8px 8px;
            }

            .mobile-close-btn {
                display: flex;
            }
            .back-to-list-btn{
                left:0;
            }
            #mainSearch {             
               flex-direction: column;
              align-items: center;
            }

        }

      @media (max-width: 400px) {

          .contact-website{
              display:none;
          }
            
          .contact-item{
              gap:0.20rem;
          }
           .back-to-list-btn{
                width:70px;
                 padding: 0.2rem 0.5rem;
            }
        }





    `];
}

declare global {
    interface HTMLElementTagNameMap {
        'find-your-senator-map': FindYourSenatorMap;
    }
}
